import { Project, Target } from '@griply/assets';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconProps } from 'shared/components/ui/icon';
import { Colors, PopupMenuListItem } from 'shared/components/ui/popup-menu';
import { NewEntityTypes, Suggestion } from 'shared/types/nlp';

import * as Styled from './suggestion-row.style';

const createIcons: Partial<Record<NewEntityTypes, IconProps['icon']>> = {
  [NewEntityTypes.Goal]: Target,
  [NewEntityTypes.Project]: Project,
};

const createLabelKeys: Record<NewEntityTypes, string> = {
  [NewEntityTypes.Goal]: 'forms.new-task.create.goal',
  [NewEntityTypes.Project]: 'forms.new-task.create.project',
  [NewEntityTypes.Tag]: 'forms.new-task.create.tag',
  [NewEntityTypes.Person]: 'forms.new-task.create.person',
};

export type SuggestionRowProps = {
  suggestion: Suggestion;
  isActive: boolean;
  onSelect: (suggestion: Suggestion) => void;
};

export const SuggestionRow = forwardRef<HTMLButtonElement, SuggestionRowProps>(
  ({ suggestion, isActive, onSelect }, ref) => {
    const { t } = useTranslation();
    const { create, name } = suggestion;
    const icon = create && createIcons[create.entityType];

    // Keeps the caret in the contentEditable input while a row is clicked.
    const preventFocusSteal = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleClick = () => onSelect(suggestion);

    return (
      <PopupMenuListItem>
        <Styled.SuggestionButton
          ref={ref}
          $isActive={isActive}
          role="option"
          aria-selected={isActive}
          tabIndex={-1}
          start={icon ? <Icon icon={icon} /> : undefined}
          startColor={create ? Colors.Primary : undefined}
          textColor={create ? Colors.Primary : undefined}
          isPremium={create?.isPremium}
          onMouseDown={preventFocusSteal}
          onClick={handleClick}
        >
          {create ? t(createLabelKeys[create.entityType], { name }) : name}
        </Styled.SuggestionButton>
      </PopupMenuListItem>
    );
  },
);
